/* Talk-to-AI dock v3 — the slide-in channel to Claude + the crew.
   Chat goes through POST /api/chat (server-side Anthropic via the Cloudflare
   AI Gateway when configured). Without ANTHROPIC_API_KEY the server answers
   live:false with an honest note and the dock keeps its "connect me" badge —
   the FOUNDER RULE. A model-router (Haiku 4.5 default · Sonnet 5 · Opus 4.8)
   picks the tier; a cost strip reads the live budget slice and offers the
   "Pause spend" cost brake (confirm + audited via useOps). "Send to Forge"
   writes a REAL task to the dispatch queue (/api/tasks → D1 + Slack mirror;
   docs/DISPATCH_BRIDGE.md) and the morning brief is composed from the live
   slices the shell already holds. */
import { useEffect, useRef, useState } from 'react';
import { IconButton, Button, Badge, Spinner } from '../../ds';
import { useOps } from '../ops';
import { useContract } from '../../lib/api';
import { tone as toneColor, toneSoft, toneLine } from '../../lib/selectors';
import type { ChatTurn } from '../../lib/contract';

interface Props {
  open: boolean;
  onClose: () => void;
  seed: ChatTurn[];
  /** Pre-composed morning brief (deriveDailyBrief upstream) — always current. */
  brief: string;
}

const SEND = 'M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z';

/* Model-router tiers — mirror functions/api/chat.ts. Default to the cheapest
   good model (Haiku 4.5); escalate to Sonnet 5; Opus 4.8 only when explicitly
   asked. The dock sends the chosen tier as `model`; the server echoes back the
   tier that actually answered (`model` on the response) so the thread can show
   which model spoke. */
type Tier = 'haiku' | 'sonnet' | 'opus';
const MODEL_LABEL: Record<Tier, string> = { haiku: 'Haiku 4.5', sonnet: 'Sonnet 5', opus: 'Opus 4.8' };
const TIERS: { id: Tier; short: string; desc: string }[] = [
  { id: 'haiku', short: 'Haiku', desc: 'fast · cheap default' },
  { id: 'sonnet', short: 'Sonnet', desc: 'escalate' },
  { id: 'opus', short: 'Opus', desc: 'rare · deep' },
];
const isTier = (v: unknown): v is Tier => v === 'haiku' || v === 'sonnet' || v === 'opus';
const fmtTok = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

type DockTurn = ChatTurn & { pending?: boolean; model?: Tier; tok?: number };

interface ChatReply {
  live: boolean;
  reply: string;
  model?: string;
  usage?: { input_tokens?: number; output_tokens?: number; cache_read_input_tokens?: number } | null;
}

export function TalkDock({ open, onClose, seed, brief }: Props) {
  const { pushToast, appendAudit, askConfirm } = useOps();
  const [chat, setChat] = useState<DockTurn[]>(seed);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [chatLive, setChatLive] = useState<boolean | null>(null); // null = unknown yet
  const [model, setModel] = useState<Tier>('haiku'); // resets to Haiku each open
  const scroller = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const asideRef = useRef<HTMLElement>(null);
  const segRefs = useRef<Record<Tier, HTMLButtonElement | null>>({ haiku: null, sonnet: null, opus: null });

  // Cost guardrail: the live budget slice. Only fetch while the dock is open —
  // no background polling behind the (inert, off-screen) closed dock.
  const { data: budget } = useContract('budget', { enabled: open });
  const hasBudget = !!budget;
  const pct = budget?.pct ?? 0;
  const unit = budget?.unit ?? '$';
  const bTone = pct >= 90 ? 'danger' : pct >= 70 ? 'warn' : 'iris';
  const bWord = pct >= 90 ? 'high' : pct >= 70 ? 'watch' : 'ok';
  const budgetAria = hasBudget
    ? `AI budget ${bWord}: ${unit}${budget!.spent} of ${unit}${budget!.cap} spent, ${pct} percent, ${budget!.daysLeft} days left${budget!.cycle ? `, ${budget!.cycle}` : ''}`
    : 'AI budget loading';

  useEffect(() => {
    if (open && scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [open, chat]);

  // Focus management: pull focus into the dock on open, return it to the opener
  // (the "Talk to AI" button) on close. The tier resets to Haiku on each open.
  useEffect(() => {
    // `inert` when closed removes the (visually off-screen) dock from tab order
    // and the a11y tree. Set imperatively — @types/react 18.3 doesn't type it yet.
    asideRef.current?.toggleAttribute('inert', !open);
    if (open) {
      openerRef.current = document.activeElement as HTMLElement | null;
      setModel('haiku');
      textareaRef.current?.focus();
    } else {
      openerRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A guarded-action confirm (the ops Dialog) owns its own Esc — don't also
      // close the dock underneath it.
      if (document.querySelector('[role="dialog"]')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /** Ship the conversation to /api/chat with the chosen tier; degrade to an
      honest local note. The server answers live:false + a canned "connect me"
      reply when ANTHROPIC_API_KEY is unset, so this path never throws on a
      missing key — it just flips the badge. */
  const askClaude = async (history: DockTurn[]) => {
    setBusy(true);
    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          model,
          messages: history
            .filter((t) => !t.pending)
            .slice(-12)
            .map((t) => ({ role: t.from === 'me' ? 'user' : 'assistant', content: t.text })),
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const body = (await res.json()) as ChatReply;
      setChatLive(body.live);
      const answered = isTier(body.model) ? body.model : undefined;
      const tok = body.usage ? (body.usage.input_tokens ?? 0) + (body.usage.output_tokens ?? 0) : undefined;
      setChat((c) => [...c.filter((t) => !t.pending), { from: 'ai', text: body.reply, model: answered, tok: tok || undefined }]);
    } catch {
      setChatLive(false);
      setChat((c) => [
        ...c.filter((t) => !t.pending),
        { from: 'ai', text: 'I couldn’t reach the chat endpoint just now — the rest of the portal is unaffected. Try again in a moment.' },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const send = () => {
    const text = input.trim();
    if (!text || busy) return;
    const next: DockTurn[] = [...chat, { from: 'me', text }, { from: 'ai', text: '…', pending: true }];
    setChat(next);
    setInput('');
    void askClaude(next);
  };

  // Roving-tabindex radiogroup: arrows move (and select) the tier, keeping a
  // single tab stop. Programmatic focus follows the new selection.
  const onSegKey = (e: React.KeyboardEvent) => {
    const dir = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!dir) return;
    e.preventDefault();
    const idx = TIERS.findIndex((t) => t.id === model);
    const next = TIERS[(idx + dir + TIERS.length) % TIERS.length].id;
    setModel(next);
    segRefs.current[next]?.focus();
  };

  const morningBrief = () => {
    if (busy) return;
    setChat((c) => [...c, { from: 'me', text: 'Give me the morning brief.' }, { from: 'ai', text: brief }]);
  };

  // Cost brake — routes through the shared guarded-action machinery: confirm
  // dialog (focus-trapped, Esc-closable) + append-only audit row + toast. The
  // executor is server-side stubbed today (coming soon), so run() resolves and
  // the row records result:'ok'; a network failure flips it to 'error'.
  const pauseSpend = () => {
    askConfirm({
      title: 'Pause all AI spend?',
      body: 'Every agent stops making paid API calls immediately. Work in progress halts mid-task until you unfreeze spend — this is the emergency cost brake.',
      confirmLabel: 'Pause spend',
      tone: 'danger',
      auditAction: 'Paused spend',
      auditTarget: 'Anthropic · all agents',
      toastTitle: 'Spend paused',
      toastMsg: 'All paid API calls frozen',
      toastTone: 'warn',
      run: async () => {
        const r = await fetch('/api/controls/pause-spend', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
        if (!r.ok) throw new Error(`pause-spend failed (${r.status})`);
      },
    });
  };

  const sendForge = async () => {
    const text = input.trim() || 'Check the portal task queue — new work from Cassidy.';
    setBusy(true);
    try {
      const res = await fetch('/api/tasks', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ op: 'create', text, from: 'Cassidy' }),
      });
      const body = (await res.json()) as { ok?: boolean; id?: string; persisted?: boolean };
      if (!res.ok || !body.ok) throw new Error('dispatch failed');
      setChat((c) => [
        ...c,
        { from: 'me', text: `Forge: ${text}` },
        {
          from: 'ai',
          text: body.persisted
            ? `Queued for the crew (${body.id}). It’s in the dispatch queue and mirrored to Slack — Forge picks it up on the next poll.`
            : 'Accepted — the queue store isn’t bound in this environment, so it wasn’t persisted. On production it lands in the crew queue.',
        },
      ]);
      setInput('');
      appendAudit({ actor: 'Cassidy', action: 'Dispatched task', target: text.slice(0, 60) });
      pushToast('success', 'Sent to the crew', body.persisted ? `Task ${body.id} queued` : 'Accepted (not persisted here)');
    } catch {
      pushToast('danger', 'Dispatch failed', 'Couldn’t reach the task queue.');
    } finally {
      setBusy(false);
    }
  };

  const selected = TIERS.find((t) => t.id === model)!;

  return (
    <>
      {open && (
        <div
          onClick={onClose}
          aria-hidden="true"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 'var(--z-overlay)' as unknown as number }}
        />
      )}
      <aside
        ref={asideRef}
        id="talk-dock"
        aria-label="Talk to AI"
        aria-hidden={!open}
        style={{
          position: 'fixed', top: 0, right: 0, height: '100dvh', width: 'min(440px, 100vw)', background: 'var(--surface)', borderLeft: '1px solid var(--border)', boxShadow: 'var(--shadow)', zIndex: 'var(--z-overlay)' as unknown as number,
          transform: open ? 'translateX(0)' : 'translateX(100%)', transition: 'transform var(--dur) var(--ease)', display: 'flex', flexDirection: 'column',
        }}
      >
        <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 16px 12px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', boxShadow: '0 0 8px var(--accent)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 15 }}>Chat with Claude</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>// crew channel</div>
          </div>
          {chatLive === true ? (
            <Badge tone="cyan">
              <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent-ink)' }} />
              live
            </Badge>
          ) : (
            <Badge tone="warn">
              <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--warn)' }} />
              connect me
            </Badge>
          )}
          <IconButton variant="ghost" size="sm" label="Close" onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </IconButton>
        </header>

        {/* Model-router: pick the tier the next question is answered on. */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 7 }}>
          <span aria-hidden="true" style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--muted)', letterSpacing: '0.05em' }}>// answer model</span>
          <div role="radiogroup" aria-label="Answer model" style={{ display: 'flex', gap: 4, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 3 }}>
            {TIERS.map((t) => {
              const sel = model === t.id;
              return (
                <button
                  key={t.id}
                  ref={(el) => { segRefs.current[t.id] = el; }}
                  role="radio"
                  aria-checked={sel}
                  aria-label={`${MODEL_LABEL[t.id]} — ${t.desc}`}
                  tabIndex={sel ? 0 : -1}
                  onClick={() => setModel(t.id)}
                  onKeyDown={onSegKey}
                  style={{
                    flex: 1, minHeight: 40, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                    fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 13,
                    color: sel ? 'var(--ink)' : 'var(--muted)',
                    background: sel ? 'var(--primary-soft)' : 'transparent',
                    border: `1px solid ${sel ? 'var(--primary-line)' : 'transparent'}`,
                    borderRadius: 'var(--radius-sm)',
                    transition: 'background var(--dur) var(--ease), color var(--dur) var(--ease)',
                  }}
                >
                  {sel && <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--primary)' }} />}
                  {t.short}
                </button>
              );
            })}
          </div>
          <span aria-hidden="true" style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--muted)' }}>
            {MODEL_LABEL[selected.id]} · {selected.desc}
          </span>
        </div>

        {/* Cost guardrails: live budget readout (dot + word, never color-only) + the pause brake. */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div role="group" aria-label={budgetAria} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
            <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: toneColor(bTone), background: toneSoft(bTone), border: `1px solid ${toneLine(bTone)}`, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: toneColor(bTone) }} />
              {bWord}
            </span>
            <span aria-hidden="true" style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
              {hasBudget ? `${unit}${budget!.spent}/${unit}${budget!.cap} · ${pct}% · ${budget!.daysLeft}d left` : 'loading…'}
            </span>
          </div>
          <Button variant="danger" size="sm" onClick={pauseSpend} style={{ minHeight: 44, marginLeft: 'auto' }}>Pause spend</Button>
        </div>

        <div ref={scroller} className="mc-scroll" style={{ flex: 1, overflow: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }} aria-live="polite">
          {chat.map((m, i) => {
            const me = m.from === 'me';
            const meta = !me && !m.pending && (m.model || m.tok != null)
              ? [m.model ? MODEL_LABEL[m.model] : null, m.tok != null ? `~${fmtTok(m.tok)} tok` : null].filter(Boolean).join(' · ')
              : null;
            return (
              <div key={i} style={{ alignSelf: me ? 'flex-end' : 'flex-start', maxWidth: '86%', display: 'flex', flexDirection: 'column', gap: 3, alignItems: me ? 'flex-end' : 'flex-start' }}>
                <div style={{ background: me ? 'var(--primary-soft)' : 'var(--surface2)', border: `1px solid ${me ? 'var(--primary-line)' : 'var(--border)'}`, borderRadius: 'var(--radius-md)', padding: '10px 12px', fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink)', whiteSpace: 'pre-wrap' }}>
                  {m.pending ? <Spinner size={14} label="Claude is thinking" /> : m.text}
                </div>
                {meta && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--muted)', padding: '0 2px' }}>{meta}</span>}
              </div>
            );
          })}
        </div>

        <div style={{ padding: '10px 12px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="secondary" size="sm" onClick={morningBrief} disabled={busy}>Morning brief</Button>
          <Button variant="secondary" size="sm" onClick={() => void sendForge()} disabled={busy}>Send Forge a task</Button>
        </div>

        <div style={{ padding: 12, borderTop: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask Claude, or brief the crew…"
            aria-label="Message"
            rows={2}
            style={{ flex: 1, resize: 'none', background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--input-border)', borderRadius: 'var(--radius-md)', padding: '9px 11px', fontFamily: 'var(--font-body)', fontSize: 13.5 }}
          />
          <IconButton variant="default" label="Send" onClick={send} disabled={busy}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d={SEND} />
            </svg>
          </IconButton>
        </div>
      </aside>
    </>
  );
}
